'use client'

import {calculateErrorMessage} from 'common/helpers'
import React from 'react'

import {parseApiResponse} from '../utils/providerParse'
import {useDynamicWalkthrough} from '../utils/useDynamicWalkthrough'
import {resolveWalkthroughTargetSync} from '../utils/walkthroughDom'
import type {WalkthroughStartOptions} from './DynamicWalkthroughContext'

type ApiResponse = any
type ParamMap = Record<string, string | number | boolean | undefined>

const replaceParamsInPath = (path: string, params: ParamMap = {}) =>
  path.replace(/:([a-zA-Z0-9_]+)/g, (_, k) => (params[k] != null ? String(params[k]) : `:${k}`))

const waitForSelector = (selector: string | undefined, timeout = 3000): Promise<boolean> => {
  if (!selector || selector === 'body') return Promise.resolve(true)
  if (String(selector).startsWith('js:')) return Promise.resolve(true)

  return new Promise((resolve) => {
    const start = Date.now()
    const check = () => {
      try {
        if (resolveWalkthroughTargetSync(selector!)) {
          return resolve(true)
        }
      } catch (err) {
        calculateErrorMessage(err)
      }
      if (Date.now() - start > timeout) {
        return resolve(false)
      }
      requestAnimationFrame(check)
    }
    check()
  })
}

type WalkthroughActions = ReturnType<typeof useDynamicWalkthrough>['actions']

let cachedActions: WalkthroughActions | null = null

const navigateFallback = async (path?: string) => {
  if (!path) return
  if (typeof window === 'undefined') return
  try {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path)
      window.dispatchEvent(new PopStateEvent('popstate'))
      await new Promise((r) => setTimeout(r, 350))
    }
  } catch (navErr) {
    calculateErrorMessage(navErr)
  }
}

const runWalkthrough = async (
  actions: WalkthroughActions,
  apiResponse: ApiResponse,
  paramMap?: ParamMap,
  options?: WalkthroughStartOptions
) => {
  if (!apiResponse?.result?.walkthrough) return
  const steps = apiResponse.result.walkthrough
  if (!Array.isArray(steps) || steps.length === 0) return

  const parsedSteps = parseApiResponse(apiResponse)
  if (!parsedSteps.length) return

  const first = steps[0]
  const rawPath: string | undefined = first?.path
  const [base, hash] = (rawPath || '').split('#')
  const resolvedBase = base ? replaceParamsInPath(base, paramMap || {}) : ''
  const resolvedPath = resolvedBase + (hash ? `#${hash}` : '')

  const firstExecution =
    (parsedSteps[0] && (parsedSteps[0] as any).execution) ??
    (first && (first as any).execution) ??
    'route'
  if (firstExecution === 'local') {
    /* empty */
  } else {
    await navigateFallback(resolvedPath)
    if (resolvedPath) {
      await new Promise((r) => setTimeout(r, 200))
    }
  }

  const resolvedSource =
    options?.source ?? (paramMap?.source === 'onboarding' ? 'onboarding' : 'chatbot')
  const sessionOptions: WalkthroughStartOptions = {
    source: resolvedSource,
    openChatOnFinish: options?.openChatOnFinish ?? (resolvedSource === 'onboarding' ? false : true),
  }
  const silent =
    (paramMap && (paramMap.source === 'onboarding' || paramMap.__silent === true)) || false

  if (silent && (actions as any).setSilentMode) {
    ;(actions as any).setSilentMode(true)
  }

  try {
    await actions.startWalkthrough(parsedSteps, apiResponse, sessionOptions)
  } catch (err) {
    calculateErrorMessage(err)
    return
  } finally {
    if (silent && (actions as any).setSilentMode) {
      ;(actions as any).setSilentMode(false)
    }
  }

  const firstTarget =
    parsedSteps[0]?.walkthrough?.target || first?.walkthrough?.target || first?.target
  let ok = false
  try {
    ok = await waitForSelector(firstTarget, 4000)
  } catch (err) {
    calculateErrorMessage(err)
  }

  if (!ok && firstTarget && !String(firstTarget).startsWith('js:')) {
    ;(actions as any).setCompletionStatus('ended')
    return
  }
  ;(actions as any).setCompletionStatus('success')
}

export const startLocalWalkthrough = async (
  script: ApiResponse,
  paramMap?: ParamMap,
  options?: WalkthroughStartOptions
) => {
  if (!cachedActions) return
  await runWalkthrough(cachedActions, script, paramMap, options)
}

export default function ChatbotWalkthroughRunner({
  apiResponse,
  paramMap,
}: {
  apiResponse: ApiResponse | null
  paramMap?: ParamMap
}) {
  const dw = useDynamicWalkthrough()

  React.useEffect(() => {
    cachedActions = dw.actions
    return () => {
      if (cachedActions === dw.actions) {
        cachedActions = null
      }
    }
  }, [dw.actions])

  React.useEffect(() => {
    if (!apiResponse?.result?.walkthrough) return

    runWalkthrough(dw.actions, apiResponse, paramMap)
  }, [apiResponse, dw.actions, paramMap])

  return null
}
